import Button from "@/components/UI/Button/Button"
import { ScryfallCard } from "@scryfall/api-types"
import {useEffect, useMemo, useState} from "react"
import { isCommander } from "@/lib/util"
import { nameToURLFormat } from "@/lib/urlFormat"

import styles from './EndMessages.module.scss'
import {GameStatsType} from "@/types/spellify/stats";
import Bar from "@/components/Spellify/EndMessages/Bar/Bar";
import { colorToSymbolMap, squareMap } from "@/lib/constants";
import Icon from "@/components/UI/Icon/Icon"
import edhrecLogo from '@/assets/images/edhrec_white.svg'
import Confetti from "./Confetti/Confetti"
import {useCookies} from "react-cookie";
import Message from "@/components/UI/Message/Message";
import {useRouter} from "next/router";
import {MODE_LABELS, MODE_PATHS, SpellifyMode} from "@/lib/useSpellifyMode";

type Props = {
    card: ScryfallCard.Normal,
    gameState: 'guessing' | 'success' | 'failure',
    guessCount: number,
    stats: GameStatsType,
    restartFunc: () => void
    mode: SpellifyMode
    spellifyNumber: number
    saltCardNumber?: number
    saltCardCount?: number
}

const iosPlatforms = ['iPhone', 'iPad', 'iPod']
const EndMessages = ({card, gameState, guessCount, stats, restartFunc, mode, spellifyNumber, saltCardNumber, saltCardCount}: Props) => {
    const checkMark = '✅'
    const xMark = '❌'
    const [copied, setCopied] = useState<string>("")
    const [isShareable, setIsShareable] = useState(false)
    const router = useRouter()
    const [cookies] = useCookies(['displayName'])
    const displayName = cookies.displayName
    const redirect = router.basePath + router.asPath

    const maxDistribution = Object.values(stats.guessHistory).sort((a,b) => a > b ? 1 : -1).reverse()[0]

    const modeName = MODE_LABELS[mode]
    const shareUrl = `https://edhrec.com/games${MODE_PATHS[mode]}`

    const shareMessage = useMemo(() => {
        const output = []
        let correctSpot = false

        let firstLine = `Spellify ${modeName} ${spellifyNumber}`.replace('  ', ' ')
        if (mode === 'salt') firstLine += ` (${saltCardNumber} of ${saltCardCount})`
        if (`${stats.spellifyNumber}` === process.env.NEXT_PUBLIC_PREVIEW_CARD_SPELLIFY_NUMBER) firstLine += ` ${process.env.NEXT_PUBLIC_PREVIEW_TAG}`

        output.push(firstLine)

        if (mode === 'hard') output.push(colorToSymbolMap['H'])
        else if (card.colors.length === 1)output.push(`${colorToSymbolMap[card.colors[0]]}`)
        else if (card.colors.length > 1) output.push(`${colorToSymbolMap['M']}`)
        else output.push(`${colorToSymbolMap['C']}`)


        let squareString = ""
        stats.guessList.forEach((item: string, index:number) => {
            if (item === "" && !correctSpot) {
                if (gameState === 'success') {
                    squareString = squareString.concat(checkMark)
                }
                else if (gameState === 'failure') {
                    squareString = squareString.concat(xMark)
                }
                correctSpot = true
            }
            else squareString = squareString.concat(squareMap[`${item}`])
        })
        output.push(squareString)
        return output.join('\n') + '\n'
    }, [card, gameState, guessCount, stats, mode, saltCardNumber])

    const writeToClipboard = (guessList: Array<'' | 'correct' | 'incorrect'>) => {
        navigator.clipboard.writeText(`${shareMessage}${shareUrl}\n`)
        setCopied("Copied!")
        setTimeout(() => setCopied(""), 1000)
    }

    useEffect(() => {
        setIsShareable(navigator.userAgent.indexOf('Firefox') === -1 && !!navigator.share && (iosPlatforms.includes(navigator.platform) || /Android/.test(navigator.userAgent)))
    }, []);

    return (
        <>
            {/* Salt mode celebrates each of its five cards, separately from the normal/hard daily confetti */}
            {mode === 'salt'
                ? <Confetti gameState={gameState} lastFiredKey="spellifySaltLastConfetti" fireId={`${spellifyNumber}-${saltCardNumber}`}/>
                : <Confetti gameState={gameState}/>}
            <div className="victoryCard">
                <div className={styles.shareButtonContainer}>
                    <a
                      href={`https://bsky.app/intent/compose?text=${encodeURIComponent(shareMessage+`\n${shareUrl}`)}`}
                      target="_blank"
                      rel="noreferrer"
                      className={styles.twitterLink}
                    >
                        <Button className={styles.blueskyButton}><Icon icon="bluesky"/> Bluesky</Button>
                    </a>
                    <a
                        href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}&url=${shareUrl}`}
                        target="_blank"
                        rel="noreferrer"
                        className={styles.twitterLink}
                    >
                        <Button className={styles.twitterButton}><Icon icon="twitter"/> Twitter</Button>
                    </a>
                    {isShareable && (
                        <Button className={`${styles.shareButton}`} type="button" onClick={() => {navigator.share({title: `Spellify ${modeName}`.trim(), text: shareMessage, url: shareUrl})}}><Icon icon="share"/> Share</Button>
                    )}
                    {!isShareable && (
                        <Button className={`${styles.shareButton}`} type="button" onClick={() => {writeToClipboard(stats.guessList)}}><Icon icon="clipboard"/> {copied ? copied :  'Copy to clipboard'}</Button>
                    )}
                    <a
                        href={`https://edhrec.com/${isCommander(card.type_line, card.oracle_text) ? 'commanders' : 'cards'}/${nameToURLFormat(card.name)}`}
                        target="_blank"
                        rel="noreferrer"
                        className={styles.edhrecLink}
                    >
                        <Button className={styles.edhrecButton} type="button"><img src={edhrecLogo.src} className={styles.edhrecLogo}></img>View on EDHREC</Button>
                    </a>
                </div>
                {!displayName &&
                    <Message type="warning">
                        <p>
                            Your stats are saved in your browser's local storage. If you want permanent saving across
                            devices, login or sign up for a free EDHREC account.
                        </p>
                        <div className={styles.buttonGroup}>
                            <a href={`/signin?utm_source=games&utm_medium=SpellifydEnd&redirect=${redirect}`}><Button color="grey">Sign In</Button></a>
                            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
                            <a href={`/signup?utm_source=games&utm_medium=SpellifydEnd&redirect=${redirect}`}><Button color="blue">Sign Up</Button></a>
                        </div>

                    </Message>
                }
                <h2 className={styles.statHeader}>{`${modeName} Statistics`.trim()}</h2>
                <div className={styles.statBlocks}>
                    <div className={styles.stat}>
                        <h2 className={styles.statValue}>{stats.gamesPlayed}</h2>
                        <h6>Played</h6>
                    </div>
                    <div className={styles.stat}>
                        <h2 className={styles.statValue}>{(stats.winRate || 0).toFixed(1)}</h2>
                        <h6>Win %</h6>
                    </div>
                    <div className={styles.stat}>
                        <h2 className={styles.statValue}>{stats.currentStreak}</h2>
                        <h6>Current Streak</h6>
                    </div>
                    <div className={styles.stat}>
                        <h2 className={styles.statValue}>{stats.maxStreak}</h2>
                        <h6>Max Streak</h6>
                    </div>
                </div>
                <div className={styles.guessGraph} >
                    {Object.entries(stats.guessHistory).map(([winNumber, count]) => (
                        <Bar winNumber={winNumber} count={count} max={maxDistribution} mostRecentWinNumber={stats.recentGuessNumber} key={winNumber}/>
                    ))}
                    
                </div>
                <h4 style={{display: "flex", justifyContent: "center", marginTop: "5px"}}>Past Performance</h4>
            </div>
        </>
    )
}

export default EndMessages